// Types
import type { ReactElement, ReactNode } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Flatpickr from 'react-flatpickr';
import DatePicker, { type DateTimePickerProps } from 'react-flatpickr';
import { usePopper } from 'react-popper';

import clsx from 'clsx';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { english } from 'flatpickr/dist/l10n/default.js';
import { Spanish } from 'flatpickr/dist/l10n/es.js';

// Context
import { TOGGLE, UPDATE_DATE_PERIOD } from '@/utils/hooks/datepicker/actions';
import { useDatepicker } from '@/utils/hooks/datepicker/useDatepicker';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import type { DatepickerDataType } from '@/utils/hooks/datepicker/types';

// Lib
import { FrenchTranslation } from '@/lib/datepicker/Translations';

// Components
import DatepickerInput from '@/components/base/Datepicker/DatepickerInput';
import DatepickerPeriods from '@/components/base/Datepicker/DatepickerPeriods';

dayjs.extend(utc);
dayjs.extend(timezone);

export type DatepickerContentProps = {
    onChange?: (args: DatepickerDataType) => void;
    isLeftIcon?: boolean;
    bottomSection?: ReactNode;
    placeholder?: string;
    noInputBorder?: boolean;
    hasBorder?: boolean;
    className?: string;
    children?: ReactNode;
    portalContainer?: Element | DocumentFragment;
};

const DatepickerContent = ({
    onChange,
    isLeftIcon,
    bottomSection,
    placeholder,
    noInputBorder,
    hasBorder = false,
    className,
    children,
    portalContainer
}: DatepickerContentProps) => {
    const { lang } = useTranslation();
    // Context
    const { datepickerData, dispatch } = useDatepicker();
    // Refs
    const datepickerRef = useRef<DatePicker>(null);
    const datepickerWrapperRef = useRef<HTMLDivElement>(null);
    const [datepickerRefInit, setDatepickerRefInit] = useState(false);
    const [referenceElement, setReferenceElement] = useState<Element>(null);
    const [popperElement, setPopperElement] = useState<Parameters<typeof usePopper>[1]>(null);
    const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
        placement: datepickerData.options.placement,
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 10]
                }
            }
        ]
    });

    const toggleDatepicker = useCallback(async () => {
        dispatch({ type: TOGGLE });
        await update();
    }, [update, dispatch]);

    const clickOuter = useCallback(
        (event: Event) => {
            if (
                popperElement?.contains(event.target as Node) ||
                referenceElement?.contains(event.target as Node) ||
                !datepickerData.isOpen
            ) {
                return;
            }

            toggleDatepicker();
        },
        [popperElement, referenceElement, toggleDatepicker, datepickerData.isOpen]
    );

    // UseEffect when a user click outer of the datepicker when it's open
    useEffect(() => {
        if (!datepickerData.isOpen) {
            document.removeEventListener('mousedown', clickOuter);
            return;
        } else {
            document.addEventListener('mousedown', clickOuter);
        }

        return () => {
            document.removeEventListener('mousedown', clickOuter);
        };
    }, [clickOuter, datepickerData.isOpen]);

    useEffect(() => {
        if (datepickerWrapperRef.current) {
            setDatepickerRefInit(true);
        }
    }, [datepickerWrapperRef]);

    useEffect(() => {
        onChange(datepickerData);
    }, [datepickerData.date, datepickerData.periodSelected]);

    const getOptions = useCallback(() => {
        const getTranslationFile = () => {
            switch (lang) {
                case 'fr':
                    return FrenchTranslation;
                case 'es':
                    return Spanish;
                case 'en':
                default:
                    return english;
            }
        };

        const currentLanguage = getTranslationFile();
        const optionsLocale =
            typeof datepickerData.options?.locale === 'object'
                ? datepickerData.options?.locale
                : {};
        return {
            appendTo: datepickerWrapperRef.current,
            inline: true,
            prevArrow:
                '<button class="w-full h-full rounded-full" type="button"><i class="icon-chevron-left" /></button>',
            nextArrow:
                '<button class="w-full h-full rounded-full" type="button"><i class="icon-chevron-right" /></button>',
            ...datepickerData?.options,
            locale: {
                ...currentLanguage,
                rangeSeparator: ' - ',
                firstDayOfWeek: datepickerData.firstDayOfWeek,
                ...optionsLocale
            }
        } as DateTimePickerProps['options'];
    }, [datepickerData?.firstDayOfWeek, datepickerData.options, lang]);

    useEffect(() => {
        if (datepickerRefInit && datepickerData?.options?.maxDate) {
            datepickerRef.current.flatpickr.set('maxDate', datepickerData.options.maxDate);
        }
    }, [datepickerData?.options?.maxDate, datepickerRefInit]);

    useEffect(() => {
        if (datepickerRefInit && datepickerData?.options?.minDate) {
            datepickerRef.current.flatpickr.set('minDate', datepickerData.options.minDate);
        }
    }, [datepickerData?.options?.minDate, datepickerRefInit]);

    const onDateChange: DateTimePickerProps['onChange'] = (selectedDates) => {
        const datesFormatted = selectedDates.map((date, index) => {
            const datesFormatted = dayjs(date).format('YYYY-MM-DD');
            if (index === 1 && datepickerData.options?.mode === 'range') {
                return dayjs(datesFormatted).endOf('day').format();
            }
            return dayjs(datesFormatted).startOf('day').format();
        });

        dispatch({
            type: UPDATE_DATE_PERIOD,
            payload: {
                date: datesFormatted,
                period: selectedDates.length === 2 ? 'custom' : null
            }
        });
    };

    return (
        <div>
            {datepickerRefInit ? (
                <Flatpickr
                    ref={datepickerRef}
                    value={datepickerData.date}
                    options={getOptions()}
                    render={(props, ref) => {
                        if (children) {
                            return React.Children.map(children, (child) => {
                                const newChild = React.cloneElement(
                                    child as ReactElement,
                                    {
                                        ...props,
                                        ref,
                                        toggleDatepicker,
                                        setReferenceElement
                                    } as React.Attributes
                                );
                                return newChild;
                            }) as unknown as ReactElement;
                        }

                        // The first ref is useful to display the button
                        // Second ref is useful for react-popper to open the datepicker
                        return (
                            <DatepickerInput
                                ref={ref}
                                placeholder={placeholder}
                                isLeftIcon={isLeftIcon}
                                noBorder={noInputBorder}
                                setReferenceElement={setReferenceElement}
                                toggleDatepicker={toggleDatepicker}
                            />
                        );
                    }}
                    onChange={onDateChange}
                />
            ) : null}

            {portalContainer ? (
                ReactDOM.createPortal(
                    <ContainerDatepicker
                        setPopperElement={setPopperElement}
                        className={className}
                        hasBorder={hasBorder}
                        styles={styles}
                        attributes={attributes}
                        datepickerWrapperRef={datepickerWrapperRef}
                        bottomSection={bottomSection}
                    />,
                    portalContainer
                )
            ) : (
                <ContainerDatepicker
                    setPopperElement={setPopperElement}
                    className={className}
                    hasBorder={hasBorder}
                    styles={styles}
                    attributes={attributes}
                    datepickerWrapperRef={datepickerWrapperRef}
                    bottomSection={bottomSection}
                />
            )}
        </div>
    );
};

type ContainerDatepickerProps = Pick<
    DatepickerContentProps,
    'className' | 'hasBorder' | 'bottomSection'
> & {
    setPopperElement: React.Dispatch<React.SetStateAction<Parameters<typeof usePopper>[1]>>;
    styles: ReturnType<typeof usePopper>['styles'];
    attributes: ReturnType<typeof usePopper>['attributes'];
    datepickerWrapperRef: React.MutableRefObject<HTMLDivElement>;
};

const ContainerDatepicker = ({
    setPopperElement,
    className,
    hasBorder,
    styles,
    attributes,
    datepickerWrapperRef,
    bottomSection
}: ContainerDatepickerProps) => {
    const { datepickerData } = useDatepicker();
    return (
        <div
            aria-label="datepicker-wrapper"
            ref={setPopperElement}
            className={clsx(
                'z-1 flex max-w-[calc(100vw-36px)] overflow-hidden overflow-x-auto rounded-md bg-white shadow-icoM transition-opacity dark:bg-icoDarkMode-deepBlack dark:shadow-icoDarkXL',
                className,
                {
                    'h-0 border-0 opacity-0 duration-200 ease-in': !datepickerData.isOpen,
                    'h-auto opacity-100 duration-150 ease-out': datepickerData.isOpen,
                    'border border-icoGray-300 dark:border-icoDarkMode-beton': hasBorder
                }
            )}
            style={styles.popper}
            {...attributes.popper}>
            {datepickerData?.periods ? <DatepickerPeriods /> : null}
            <div
                className={clsx({
                    'px-8 pb-8 pt-4': datepickerData.periods,
                    'px-5 pb-5 pt-4': !datepickerData?.periods
                })}>
                <div className="mb-3 mt-1.5" ref={datepickerWrapperRef} />
                {bottomSection}
            </div>
        </div>
    );
};

export default DatepickerContent;
